import React from "react";
import UseAlert from "@/js/common/ui/alert/UseAlert";
import Spinner, { spinnerType } from "@/js/common/ui/Spinner";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import { useResetRecoilState, useSetRecoilState } from "recoil";
import * as Atom from "@/js/common/Atom";
import Util from "@/js/common/Util";
import { useNavigate, useLocation } from "react-router-dom";
import Toast, { NotificationToast } from "@/js/common/ui/Toast";
import Body from "./layout/Body";
import ToolTip from "./common/ui/ToolTip";
import { getTokenCrtDt } from "./layout/TokenCheck";
import { removeCookie } from "@/js/common/Cookie";

function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const { showAlert } = UseAlert();
    const [landerCmplt, setLanderCmplt] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [loadingDim, setLoadingDim] = React.useState<boolean>(false);
    const [dimOpacity, setDimOpacity] = React.useState<number>();
    const [spinnerType, setSpinnerType] = React.useState<spinnerType>();
    const setUserInfo = useSetRecoilState(Atom.userInfo);
    const delUserInfo = useResetRecoilState(Atom.userInfo);

    React.useEffect(() => {
        if (landerCmplt) {
            return;
        }
        if (import.meta.env.VITE_APP_ENV === "local") {
            AppContext.backendUrl = "http://" + window.location.hostname + ":" + import.meta.env.VITE_APP_BACKEND_PORT + import.meta.env.VITE_APP_BACKEND_CONTEXT_PATH;
        } else {
            AppContext.backendUrl = window.location.origin + import.meta.env.VITE_APP_BACKEND_CONTEXT_PATH;
        }
        AppContext.showSpinner = (type?: spinnerType, dimVal?: number) => {
            setLoading(true);
            setSpinnerType(type);
            if (dimVal) {
                setLoadingDim(true);
                setDimOpacity(dimVal);
            } else {
                setLoadingDim(false);
            }
        };
        AppContext.hideSpinner = () => {
            setLoading(false);
            setLoadingDim(false);
        };
        AppContext.openAlert = ({
            title,
            msg,
            icon,
            closeText,
            handleClose,
            confirmText,
            handleConfirm,
        }: any) => {
            showAlert({
                title: title,
                message: msg,
                iconFg: icon,
                closeText: closeText,
                handleClose: handleClose,
                confirmText: confirmText,
                handleConfirm: handleConfirm,
            });
        };
        AppContext.goLogout = () => {
            delUserInfo(); // 유저정보 초기화
            removeCookie("tokenCrtDt");
            localStorage.removeItem("tokenCrtDt");
            localStorage.removeItem("grantType");
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("isAdmin");
            // setIsAdmin(false);
            navigate("/portal/login");
        };
    }, [landerCmplt, delUserInfo, navigate, showAlert]);

    React.useEffect(() => {
        if (landerCmplt) {
            return;
        }
        Axios.dataAccess({
            url: "common/get-system-info.do",
            methodType: "post",
            onSuccessFn: (res: any) => {
                AppContext.siteCode = res.item.siteCode;
                AppContext.fileWhiteExt = res.item.fileWhiteExt;
                AppContext.maxfileSize = res.item.maxfileSize;
                AppContext.accessTokenExpireTime = res.item.accessTokenExpireTime;
                AppContext.refreshTokenExpireTime = res.item.refreshTokenExpireTime;
                AppContext.codeList = res.item.codeList; // 코드
                AppContext.ognzList = res.item.ognzList; // 조직코드(본부, 지부)
                AppContext.bgtSbjctList    = res.item.bgtSbjctList   ; // 운영비 관항목 코드
                AppContext.acntgYearList   = res.item.acntgYearList  ; // 회계연도
                AppContext.spcBgtSbjctList = res.item.spcBgtSbjctList; // 특별회계기금 관항목 코드
                AppContext.ovrlOgnzList    = res.item.ovrlOgnzList;    // 특별회계기금 관항목 코드
                const tokenCrtDt = getTokenCrtDt();
                if (tokenCrtDt !== null && tokenCrtDt !== undefined) {
                    // 토큰정보 있으면
                    Axios.dataAccess({
                        url: "system/user/select-user-info.do",
                        methodType: "post",
                        onSuccessFn: (res2: any) => {
                            if (Util.isEmpty(res2.item)) {
                                // 서버에 인증정보 없으면
                                AppContext.goLogout(); // 로그아웃 처리
                                setLanderCmplt(true);
                                return;
                            }
                            // 유저 정보 세팅
                            setUserInfo(res2.item);
                            // setIsAdmin(res2.item.mngrYn === "Y");
                            localStorage.setItem("isAdmin", res2.item.mngrYn === "Y" ? "1" : "0");
                            if (res2.item.mngrYn !== "Y" && location.pathname.startsWith("/admin", 0)) {
                                // 관리자가 아닌데 관리자 경로 접근할 경우
                                navigate("/"); // 메인페이지로
                            } else {
                                navigate(location.pathname); // 지도페이지로
                            }
                            setLanderCmplt(true);
                        }
                    });
                } else {
                    localStorage.removeItem("tokenCrtDt");
                    removeCookie("tokenCrtDt");
                    // 토큰정보 없으면
                    if (!location.pathname.startsWith("/portal", 0)) {
                        navigate("/"); // 메인페이지로
                    }
                    setLanderCmplt(true);
                }
            },
            onFailedFn: () => {
                setLanderCmplt(true);
            }
        });
    }, [landerCmplt, location, setUserInfo, navigate]);

    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [location.pathname]);

    if (!landerCmplt) {
        return (
            <Spinner loading={true} loadingDim={true} dimOpacity={0.3} spinnerType="1" />
        )
    } else {
        return (
            <React.Fragment>
                <Body />
                <Spinner loading={loading} loadingDim={loadingDim} dimOpacity={dimOpacity} spinnerType={spinnerType} />
                <Toast />
                <NotificationToast />
                <ToolTip id="gridTableTip" place="left" />
            </React.Fragment>
        );
    }
}
export default App;